import { useCallback, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';

import {
  ContractBoardColumnDocument,
  ContractBoardColumnQuery,
  ContractDocumentCardQuery,
  ContractDocumentsListQuery,
  ContractDocumentsListQueryVariables,
  useContractBoardColumnAmountsQuery,
  useContractBoardColumnQuery,
  useContractDocumentCardQuery,
  useContractDocumentsListQuery,
  useContractDocumentsQuickCountQuery,
} from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { contractAmountRows } from './contractsBoardModel';
import { isEsignEmailPending, isEsignInFlight } from './esignModel';
import { useEsignPermissions } from './useEsignPermissions';

export type IContractDocumentRow = ContractDocumentsListQuery['contractDocuments'][number];

/** ТЗ 0128: лёгкая строка карточки доски (без реквизитов строки таблицы). */
export type IContractBoardRow = ContractBoardColumnQuery['boardColumn'][number];

export type IContractDocumentCard = NonNullable<
  ContractDocumentCardQuery['contractDocument']
>;

export type IContractDocumentsFilters = Omit<
  ContractDocumentsListQueryVariables,
  'limit' | 'offset'
>;

/** Опрос раз в 5 с — только пока на экране есть строка «в пути». */
export const ESIGN_POLL_MS = 5000;

const EMPTY_ROWS: IContractDocumentRow[] = [];
const EMPTY_BOARD_ROWS: IContractBoardRow[] = [];

/** ТЗ 0128: страница колонки доски — первая и каждая дозагрузка. */
export const CONTRACT_BOARD_PAGE = 30;

export interface IUseContractDocuments {
  rows: IContractDocumentRow[];
  total: number;
  /** Сколько договоров в каждом статусе при прочих фильтрах. */
  statusCounts: Record<string, number>;
  /**
   * ТЗ 0120: из них с шансом лида ≥ `hotMinWinChance` (только роли лидов;
   * переменная не передана — сервер берёт 0,7; не роли лидов — нули).
   */
  hotCounts: Record<string, number>;
  /** ТЗ 0127: из них с «хорошим» шансом (≥ 0,55 и ниже горячих). */
  goodCounts: Record<string, number>;
  /** Есть выдача (текущая или прошлая, пока едет новая); нет — счётчики «—». */
  ready: boolean;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Список договоров с фильтрами и страницей (`limit`/`offset`).
 *
 * Страница — это ПЕРЕМЕННЫЕ запроса, а не `fetchMore`-склейка: опрос и
 * перезапрос перечитывают ровно текущую страницу и не схлопывают
 * накопленное (грабли `apollo-refetch-collapses-pages`).
 *
 * При смене фильтра данные для новых переменных ещё не пришли — таблица
 * держит прошлую выдачу (`previousData`), а не мигает пустотой: иначе
 * пустое состояние на полсекунды читается как «ничего не нашлось».
 *
 * `enabled=false` — вызывающий не имеет прав на домен (карточка, которую
 * открывает посторонняя роль): запрос не уходит вовсе.
 */
export const useContractDocuments = (
  filters: IContractDocumentsFilters,
  limit: number,
  offset: number,
  enabled = true,
  /**
   * ТЗ 0120: счётчики статусов. Колонки доски их не спрашивают — страница
   * берёт их одним запросом (`statusCounts`/`hotCounts` тогда пустые).
   */
  withCounts = true,
): IUseContractDocuments => {
  const { canOpenPage } = useEsignPermissions();
  const skip = !enabled || !canOpenPage;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useContractDocumentsListQuery({
    variables: { ...filters, limit, offset, withCounts },
    skip,
    fetchPolicy: 'cache-and-network',
    // ТЗ 0128: после первого ответа — из кэша; иначе Apollo 3.3 перечитывает
    // `cache-and-network` из сети на каждую чужую запись в кэш.
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
  });

  // Прошлая выдача (другие фильтры) — только пока едет ответ на новые. Упал
  // ТЕКУЩИЙ запрос — её не выдаём за актуальную: пусто и ошибка «Повторить».
  const shown = data ?? (error ? undefined : previousData);
  const rows = shown?.contractDocuments ?? EMPTY_ROWS;
  const counts = shown?.contractDocumentStatusCounts;
  const statusCounts = useMemo(
    () =>
      Object.fromEntries((counts ?? []).map((row) => [row.status, row.count])),
    [counts],
  );
  const hotCounts = useMemo(
    () =>
      Object.fromEntries(
        (counts ?? []).map((row) => [row.status, row.hotCount ?? 0]),
      ),
    [counts],
  );
  const goodCounts = useMemo(
    () =>
      Object.fromEntries(
        (counts ?? []).map((row) => [row.status, row.goodCount ?? 0]),
      ),
    [counts],
  );
  const inFlight = rows.some(isEsignInFlight);

  usePageVisiblePoll(
    startPolling,
    stopPolling,
    ESIGN_POLL_MS,
    !skip && inFlight,
  );

  return {
    rows,
    total: shown?.contractDocumentsTotal ?? 0,
    statusCounts,
    hotCounts,
    goodCounts,
    ready: Boolean(shown),
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/**
 * ТЗ 0120, этап 2 → ТЗ 0128 — только ЧИСЛО договоров под фильтрами
 * (мини-метрики шапки и числа на быстрых чипах): `contractDocumentsCount` —
 * без строк и счётчиков статусов.
 * `null` — ответа ещё нет (или нет прав): «0» читался бы как «пусто».
 */
export const useContractDocumentsCount = (
  filters: IContractDocumentsFilters,
  enabled = true,
): number | null => {
  const { canOpenPage } = useEsignPermissions();
  const variables = useMemo(() => {
    // Сортировка и пороги шанса числу не нужны (их нет в операции).
    const rest = { ...filters };
    delete rest.sort;
    delete rest.hotMinWinChance;
    delete rest.goodMinWinChance;
    delete rest.withCounts;
    return rest;
  }, [filters]);
  const { data, previousData, error } = useContractDocumentsQuickCountQuery({
    variables,
    skip: !enabled || !canOpenPage,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const shown = data ?? (error ? undefined : previousData);
  return shown ? shown.contractDocumentsCount : null;
};

/**
 * ТЗ 0128 — колонка доски договоров: лёгкие карточки страницами по
 * `CONTRACT_BOARD_PAGE`; «ещё» — `fetchMore` следующей страницы (политика
 * кэша `boardColumn` дописывает её, без потолка 200 и без перечитывания с
 * нуля), `total` — `contractDocumentsCount` колонки. Пока на экране есть
 * договор «в пути», колонка опрашивается раз в 5 с — только видимая вкладка
 * (`usePageVisiblePoll`); опрос перечитывает голову колонки, хвост остаётся.
 */
export const useContractBoardColumn = (
  filters: IContractDocumentsFilters,
  enabled = true,
) => {
  const { canOpenPage } = useEsignPermissions();
  const skip = !enabled || !canOpenPage;
  const variables = useMemo(() => {
    const rest = { ...filters };
    delete rest.hotMinWinChance;
    delete rest.goodMinWinChance;
    delete rest.withCounts;
    return { ...rest, offset: 0, limit: CONTRACT_BOARD_PAGE };
  }, [filters]);
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
    fetchMore,
    startPolling,
    stopPolling,
  } = useContractBoardColumnQuery({
    variables,
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
  });
  const shown = data ?? (error ? undefined : previousData);
  const rows = shown?.boardColumn ?? EMPTY_BOARD_ROWS;
  const total = shown?.contractDocumentsCount ?? 0;
  const loadedRef = useRef(0);
  loadedRef.current = data?.boardColumn.length ?? 0;

  const inFlight = rows.some(isEsignInFlight);
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    ESIGN_POLL_MS,
    !skip && inFlight,
  );

  const [moreBusy, setMoreBusy] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const moreRef = useRef(false);
  const loadMore = useCallback(() => {
    if (moreRef.current || !loadedRef.current) return;
    moreRef.current = true;
    setMoreBusy(true);
    setMoreFailed(false);
    fetchMore({
      variables: { offset: loadedRef.current, limit: CONTRACT_BOARD_PAGE },
    })
      .catch(() => setMoreFailed(true))
      .finally(() => {
        moreRef.current = false;
        setMoreBusy(false);
      });
  }, [fetchMore]);

  // Перечитать всё загруженное одним запросом (колонка не схлопывается).
  const client = useApolloClient();
  const reload = useCallback(() => {
    if (skip) return;
    client
      .query({
        query: ContractBoardColumnDocument,
        variables: {
          ...variables,
          limit: Math.max(CONTRACT_BOARD_PAGE, loadedRef.current),
        },
        fetchPolicy: 'network-only',
      })
      .catch(() => refetch().catch(() => undefined));
  }, [client, refetch, skip, variables]);

  return {
    rows,
    total,
    ready: Boolean(shown),
    loading,
    error,
    hasMore: Boolean(shown) && rows.length < total && !error,
    loadMore,
    moreBusy,
    moreFailed,
    reload,
  };
};

/**
 * ТЗ 0120, этап 2 — точная сумма колонки доски по валютам (все договоры
 * колонки под фильтрами, а не только загруженные карточки). PLN первой,
 * без нулевых; `null` — ответа ещё нет.
 */
export const useContractColumnAmounts = (
  filters: IContractDocumentsFilters,
  enabled = true,
) => {
  const { canOpenPage } = useEsignPermissions();
  const variables = useMemo(() => {
    // Сортировка и порог «горячих» сумме не нужны (их нет в операции).
    const rest = { ...filters };
    delete rest.sort;
    delete rest.hotMinWinChance;
    delete rest.goodMinWinChance;
    delete rest.withCounts;
    return rest;
  }, [filters]);
  const { data, previousData, error } = useContractBoardColumnAmountsQuery({
    variables,
    skip: !enabled || !canOpenPage,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const shown = data ?? (error ? undefined : previousData);
  const amounts = shown?.contractDocumentAmounts;
  return useMemo(() => (amounts ? contractAmountRows(amounts) : null), [
    amounts,
  ]);
};

export interface IUseContractDocument {
  contract: IContractDocumentCard | null;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Карточка одного договора (ящик «Договор»).
 *
 * Опрашивается, пока идёт отправка или зачисление: оба процесса живут в
 * Celery, и без опроса человек смотрел бы на «Отправляется» до перезагрузки.
 */
export const useContractDocument = (
  id: string | null,
): IUseContractDocument => {
  const { canOpenPage } = useEsignPermissions();
  const skip = !id || !canOpenPage;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useContractDocumentCardQuery({
    variables: { id: id || '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const contract = data?.contractDocument ?? null;
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    ESIGN_POLL_MS,
    !skip &&
      Boolean(
        contract &&
          (isEsignInFlight(contract) || isEsignEmailPending(contract)),
      ),
  );

  return {
    contract,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};
